import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import { api } from '../services/api';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import logo from '../assets/images/logo.jpg';
import { useLanguage } from '../translations/LanguageContext';

interface LocationState {
  score: number;
  time: string;
  gameSessionId: string;
}

const GameEndPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useLanguage();
  const { score, time, gameSessionId } = location.state as LocationState;
  const [playerName, setPlayerName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await api.submitScore(playerName || null, score, time, gameSessionId);
      navigate('/leaderboard');
    } catch (err) {
      setError(translations.gameEnd.failedToSave);
      setIsSaving(false);
    }
  };

  const handlePlayAgain = () => {
    navigate('/game');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          mb: 4,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <img 
            src={logo} 
            alt="EcoQuiz Logo" 
            style={{ 
              width: '100px', 
              height: 'auto',
              marginBottom: '16px',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          />
          <Typography variant="h3" color="primary.main" align="center" gutterBottom style={{marginBottom: '0px'}}>
            {translations.gameEnd.greatJob}
          </Typography>
          <Typography variant="h3" color="primary.main" align="center" gutterBottom style={{marginTop: '0px'}}>
            {translations.gameEnd.ecoWarrior}
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 1,
            mb: 2
          }}>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 500 }}>
              {score} {translations.gameEnd.points}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {translations.gameEnd.time}: {time}
            </Typography>
          </Box>

          <TextField
            fullWidth
            label={translations.gameEnd.yourName}
            variant="outlined"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            disabled={isSaving}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={<SaveIcon />}
            >
              {translations.gameEnd.saveScore}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handlePlayAgain}
              disabled={isSaving}
              startIcon={<ReplayIcon />}
            >
              {translations.gameEnd.playAgain}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default GameEndPage; 